// src/app/services/orchestrator-api.service.ts
import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = process.env.NEXT_PUBLIC_ORCHESTRATOR_API_URL;

// ============================================================================
// INTERFACES (aligned with backend DTOs)
// ============================================================================

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  email: string;
  isSuperAdmin: boolean;
}

export interface RegisterUserRequest {
  email: string;
  password: string;
  isSuperAdmin?: boolean;
}

export interface UserAccount {
  id: number;
  email: string;
  isSuperAdmin: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  // Present on GET /api/admin/users (AdminUsersController.GetAllUsers) — the companies this user
  // belongs to, for rendering as pills. Superadmin/other single-user endpoints may omit it.
  companies?: UserListCompany[];
}

export interface UserListCompany {
  compId: number;
  compCode: string;
  compName: string;
  isCompAdmin: boolean;
}

export interface KOrg {
  id: number;
  name: string;
  code: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface Comp {
  id: number;
  kOrgId: number;
  name: string;
  code: string;
  epicorCompanyId?: string;
  googleCompanyCode?: string;
  googleDriveRootFolderId?: string;
  displayName?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CompConfig {
  epicorServerInstance?: string;
  epicorUsername?: string;
  epicorPassword?: string;
  epicorXApiKey?: string;
  googleDriveRootFolderId?: string;
  externalCompanyCode?: string;
  // docs/po-drive-first-plan.md P1 — Shared Drive id PoMatchService searches for this company's
  // customer PO PDFs. Blank falls back to PoMigration:DriveIds:{Company} in appsettings.json.
  poDriveFolderId?: string;
  // E5 — surfaced by GetCompConfig, read-only (a save that passes Test Connection clears these
  // automatically; UpdateCompConfig ignores them if sent back).
  epicorAuthFailedAt?: string | null;
  epicorLastAuthError?: string | null;
}

// Backend: KOrgUser entity
export interface KOrgUser {
  id: number;
  userEmail: string;
  kOrgId: number;
  isOrgAdmin: boolean;
}

// Backend: KOrgUserDto (for POST requests)
export interface KOrgUserDto {
  userEmail: string;
  isOrgAdmin: boolean;
}

// Response when fetching user's orgs (from AdminKOrgUsersController)
export interface UserOrg {
  id: number;           // KOrgUser.Id (link ID)
  userEmail: string;
  kOrgId: number;
  kOrgName: string;     // From joined KOrg
  kOrgCode: string;
  isOrgAdmin: boolean;
}

// Backend: CompUser entity (Track A — per-company user access)
export interface CompUser {
  id: number;
  userEmail: string;
  compId: number;
  isCompAdmin: boolean;
}

// Backend: CompUserDto (for POST requests)
export interface CompUserDto {
  userEmail: string;
  isCompAdmin: boolean;
  // KAIROSTSAP-100 — only used when userEmail has no existing account yet; creates one. Ignored
  // (and not required) when the account already exists.
  password?: string;
}

// Response when fetching user's companies (from AdminUsersController.GetUserComps)
export interface UserComp {
  id: number;           // CompUser.Id (link ID)
  userEmail: string;
  compId: number;
  compName: string;     // From joined Comp
  compCode: string;
  isCompAdmin: boolean;
}

// Response from GET /api/user/my-companies
export interface MyCompany {
  id: number;
  name: string;
  code: string;
  externalCompanyCode?: string;
  displayName?: string;
  // KAIROSTSAP-100 — true only if the calling user actually administers this company (or is
  // superadmin), not merely a member of it. Use this to restrict which companies a company-admin
  // viewer may add a new user into.
  isCompAdmin: boolean;
}

// ============================================================================
// INVOICE TRACKING INTERFACES
// ============================================================================

export interface InvoiceProcessingLogEntry {
  id: string;
  timestamp: string;
  timestampRaw?: string;
  action: string;
  details: string;
  /**
   * Who performed the action — the signed-in user's email, or "Automated (client-id)" for an
   * API-key call. Absent on entries recorded before this was tracked; show nothing for those
   * rather than guessing.
   */
  performedBy?: string | null;
}

export interface InvoiceListItem {
  id: string;
  invoiceNumber: string;
  legalNumber: string;
  customerId: string;
  customerName: string;
  invoiceDate: string;
  amount: number;
  status: string;
  documentCount: number;
  documentsDownloaded: number;
  driveUrl?: string;
  emailSentAt?: string;
  // ISO/UTC — see formatSgtTime in invoices/page.tsx for the "Sent by X · HH:mm SGT" display.
  emailSentAtRaw?: string | null;
  errorMessage?: string;
  lastModified: string;
  lastAction?: string;
  // Duplicate-send incident, 15 Sep 2026 — who performed lastAction, so a sent row can read
  // "Sent by Carol · 08:27 SGT" instead of just "Sent".
  lastActionBy?: string | null;
  // Set (to a person's email) while a send is in flight for this invoice, cleared when it
  // finishes or fails. Stale after 5 minutes (server-side truth; the frontend applies the same
  // window) so a crashed send never looks permanently busy.
  sendClaimedAt?: string | null;
  sendClaimedBy?: string | null;
  orderNum?: string;
  packNum?: string;
  // T1-A, docs/epicor-load-plan.md — stored, not a live Epicor call per row. null means the
  // background sweep / an open / a generate hasn't checked this invoice yet, not "zero attachments".
  doCount?: number | null;
  poCount?: number | null;
  needsDO?: boolean;
  needsPO?: boolean;
  docCountsCheckedAt?: string | null;
  // 23 Sep 2026 — what went into the CURRENT PDF, as opposed to doCount/poCount above (what Epicor
  // holds). The Docs badge reads these; doCount/poCount only decide whether a regenerate would
  // actually add something. null = never recorded, render as unknown, never as zero.
  mergedDoCount?: number | null;
  mergedPoCount?: number | null;
  mergedAt?: string | null;
}

export interface InvoiceDetail extends InvoiceListItem {
  // The invoice's real Epicor company (docs/pdf-summary-company-param-fix.md) — only present on
  // the detail response, not the list. Use this, never the `?company=` query string, for any
  // Epicor-facing call made from the detail/pdf-summary page.
  company: string;
  createdAt: string;
  processedAt?: string;
  processingLog: InvoiceProcessingLogEntry[];
}

export interface InvoicesResponse {
  total: number;
  /** Count after the status filter (Q#2); drives pagination. Equals `total` when unfiltered. */
  filteredTotal?: number;
  limit: number;
  offset: number;
  statusCounts: Record<string, number>;
  invoices: InvoiceListItem[];
}

// ============================================================================
// SERVICE
// ============================================================================

// --- PO backfill (Ken 2026-07-27, order-driven 2026-08-03) ---
export interface PoCandidate {
  fileId: string;
  fileName: string;
  webViewLink?: string | null;
  parsedCustId?: string | null;
  parsedPoNum?: string | null;
  confidence: 'High' | 'Medium' | 'Low' | 'None';
  matchedOn: string;
}

/** Ready = High confidence and exactly one candidate, the only case safe to auto-attach.
 *  NeedsReview = matched but ambiguous. NoMatch = nothing in Drive.
 *  Skipped = the PO number isn't a real customer PO, so there is nothing to look for. */
export type PoOrderOutcome = 'Ready' | 'NeedsReview' | 'NoMatch' | 'Skipped';

export interface PoOrderMatchResult {
  company: string;
  orderNum: number;
  custId: string;
  customerName?: string | null;
  orderPoNum?: string | null;
  orderDate?: string | null;
  openOrder: boolean;
  outcome: PoOrderOutcome;
  summary: string;
  bestMatch?: PoCandidate | null;
  candidates: PoCandidate[];
}

export interface OrderScanResult {
  company: string;
  from: string;
  to: string;
  /** Orders the BAQ returned before the scan limit was applied. */
  totalFound: number;
  /** Orders actually matched in this response. */
  scanned: number;
  /** True when totalFound exceeded the limit — narrow the range to see the rest. */
  truncated: boolean;
  readyCount: number;
  needsReviewCount: number;
  noMatchCount: number;
  skippedCount: number;
  elapsedMs: number;
  orders: PoOrderMatchResult[];
}
export interface CustomerMissingPo {
  custId: string;
  customerName?: string | null;
  /** Every order the BAQ returned for this customer in the range. */
  orderCount: number;
  /** Orders whose PO number actually looks like a customer PO — the rest would only be Skipped. */
  matchableCount: number;
  oldestOrderDate?: string | null;
  newestOrderDate?: string | null;
}

export interface CustomerScanSummary {
  company: string;
  from: string;
  to: string;
  totalOrders: number;
  totalMatchable: number;
  elapsedMs: number;
  /** Already sorted by the backend: most matchable work first. */
  customers: CustomerMissingPo[];
}

export interface PoAttachResult {
  success: boolean;
  xFileRefNum: number;
  fileName: string;
  docTypeID: string;
  message: string;
  /** True when the PO was already on the sales order and nothing new was uploaded.
   *  success is still true — show this as a warning, not as a fresh attach. */
  alreadyAttached?: boolean;
}

class OrchestratorApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add token to requests if available
    this.api.interceptors.request.use((config) => {
      const token = this.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle expired / invalid sessions globally
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        // The login request itself has no session to expire — a 401 here means bad
        // credentials or a deactivated account, and the login page already shows a
        // persistent error for that. Redirecting would blow away that message after
        // ~100ms and replace it with a misleading "session expired" banner.
        const isLoginRequest = error.config?.url?.includes('/api/auth/login');
        if (error.response?.status === 401 && !isLoginRequest && typeof window !== 'undefined') {
          this.clearToken();
          // Small delay so any in-flight state settles before redirect
          setTimeout(() => {
            window.location.href = '/auth/login?reason=session_expired';
          }, 100);
        }
        return Promise.reject(error);
      }
    );
  }

  // --------------------------------------------------------------------------
  // Token management
  // --------------------------------------------------------------------------
  setToken(token: string) {
    if (typeof window !== 'undefined') {
      localStorage.setItem('orchestrator_token', token);
    }
  }

  getToken(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('orchestrator_token');
    }
    return null;
  }

  clearToken() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('orchestrator_token');
      localStorage.removeItem('orchestrator_is_admin');
    }
  }

  setIsAdmin(isAdmin: boolean) {
    if (typeof window !== 'undefined') {
      localStorage.setItem('orchestrator_is_admin', isAdmin ? '1' : '0');
    }
  }

  getIsAdmin(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      const token = localStorage.getItem('orchestrator_token');
      if (!token) return false;
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      // JWT claim may be string "true" or boolean true
      return decoded.isSuperAdmin === true || decoded.isSuperAdmin === 'true';
    } catch {
      return false;
    }
  }

  /**
   * True when this user administers at least one company (CompUser.IsCompAdmin), even if they are
   * not superadmin. Gates access to /admin/users specifically — every other /admin/* page stays
   * superadmin-only. See AdminLayout and NavigationContextProvider.
   */
  getIsCompAdmin(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      const token = localStorage.getItem('orchestrator_token');
      if (!token) return false;
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return decoded.isCompAdmin === true || decoded.isCompAdmin === 'true';
    } catch {
      return false;
    }
  }

  /**
   * True when the password was set by someone else (admin creation or an admin reset) and the
   * user must choose their own before doing anything else. See docs/password-management-plan.md.
   */
  getMustChangePassword(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      const token = localStorage.getItem('orchestrator_token');
      if (!token) return false;
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return decoded.mustChangePassword === true || decoded.mustChangePassword === 'true';
    } catch {
      return false;
    }
  }

  /**
   * True when the logged-in user is internal Kairos staff, determined by their
   * email domain (the JWT carries `email`/`sub`). Used to gate Phase 2 features so
   * client users don't see them even when a feature flag is on. Note: super-admin
   * is NOT a reliable signal here because some client users are also admins.
   */
  getIsInternalUser(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      const token = localStorage.getItem('orchestrator_token');
      if (!token) return false;
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      const email = String(decoded.email ?? decoded.sub ?? '').toLowerCase();
      return email.endsWith('@kairossolutions.co');
    } catch {
      return false;
    }
  }

  // --------------------------------------------------------------------------
  // Auth endpoints
  // --------------------------------------------------------------------------
  async changePassword(currentPassword: string, newPassword: string): Promise<{ token: string }> {
    const response = await this.api.post<{ token: string }>('/api/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  }

  async resetUserPassword(userId: number, newPassword: string): Promise<void> {
    await this.api.post(`/api/admin/users/${userId}/reset-password`, { newPassword });
  }

  async resetCompUserPassword(compId: number, userId: number, newPassword: string): Promise<void> {
    await this.api.post(`/api/admin/comps/${compId}/users/${userId}/reset-password`, { newPassword });
  }

  async login(request: LoginRequest): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/api/auth/login', request);
    return response.data;
  }

  async validate() {
    const response = await this.api.get('/api/auth/validate');
    return response.data;
  }

  // --------------------------------------------------------------------------
  // User management (AdminUsersController)
  // --------------------------------------------------------------------------
  async getAllUsers(): Promise<UserAccount[]> {
    const response = await this.api.get<UserAccount[]>('/api/admin/users');
    return response.data;
  }

  async getUser(id: number): Promise<UserAccount> {
    const response = await this.api.get<UserAccount>(`/api/admin/users/${id}`);
    return response.data;
  }

  async createUser(request: RegisterUserRequest): Promise<UserAccount> {
    const response = await this.api.post<UserAccount>('/api/admin/users', request);
    return response.data;
  }

  async updateUserActiveStatus(userId: number, isActive: boolean): Promise<void> {
    await this.api.patch(`/api/admin/users/${userId}/active`, { isActive });
  }

  async deleteUser(userId: number): Promise<void> {
    await this.api.delete(`/api/admin/users/${userId}`);
  }

  // Get orgs for a user (by email)
  async getUserOrgs(userId: number): Promise<UserOrg[]> {
    const response = await this.api.get<UserOrg[]>(`/api/admin/users/${userId}/orgs`);
    return response.data;
  }

  // Get companies for a user (by email) — mirrors getUserOrgs above
  async getUserComps(userId: number): Promise<UserComp[]> {
    const response = await this.api.get<UserComp[]>(`/api/admin/users/${userId}/comps`);
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Org management (AdminKOrgController)
  // --------------------------------------------------------------------------
  async getKOrgs(): Promise<KOrg[]> {
    const response = await this.api.get<KOrg[]>('/api/admin/korgs');
    return response.data;
  }

  async getKOrg(id: number): Promise<KOrg> {
    const response = await this.api.get<KOrg>(`/api/admin/korgs/${id}`);
    return response.data;
  }

  async createKOrg(org: Partial<KOrg>): Promise<KOrg> {
    const response = await this.api.post<KOrg>('/api/admin/korgs', org);
    return response.data;
  }

  async updateKOrg(id: number, org: Partial<KOrg>): Promise<void> {
    await this.api.put(`/api/admin/korgs/${id}`, org);
  }

  async deleteKOrg(id: number): Promise<void> {
    await this.api.delete(`/api/admin/korgs/${id}`);
  }

  // --------------------------------------------------------------------------
  // Org users (AdminKOrgUsersController)
  // --------------------------------------------------------------------------
  async getOrgUsers(kOrgId: number): Promise<KOrgUser[]> {
    const response = await this.api.get<KOrgUser[]>(`/api/admin/korgs/${kOrgId}/users`);
    return response.data;
  }

  async addUserToOrg(kOrgId: number, dto: KOrgUserDto): Promise<KOrgUser> {
    const response = await this.api.post<KOrgUser>(`/api/admin/korgs/${kOrgId}/users`, dto);
    return response.data;
  }

  async removeUserFromOrg(kOrgId: number, linkId: number): Promise<void> {
    await this.api.delete(`/api/admin/korgs/${kOrgId}/users/${linkId}`);
  }

  // --------------------------------------------------------------------------
  // Company users (AdminCompUsersController) — mirrors Org users above
  // --------------------------------------------------------------------------
  async getCompUsers(compId: number): Promise<CompUser[]> {
    const response = await this.api.get<CompUser[]>(`/api/admin/comps/${compId}/users`);
    return response.data;
  }

  async addUserToComp(compId: number, dto: CompUserDto): Promise<CompUser> {
    const response = await this.api.post<CompUser>(`/api/admin/comps/${compId}/users`, dto);
    return response.data;
  }

  async removeUserFromComp(compId: number, linkId: number): Promise<void> {
    await this.api.delete(`/api/admin/comps/${compId}/users/${linkId}`);
  }

  // GET /api/user/my-companies — all companies the current user may see (superadmin gets all)
  async getMyCompanies(): Promise<MyCompany[]> {
    const response = await this.api.get<MyCompany[]>('/api/user/my-companies');
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Companies (AdminCompController)
  // --------------------------------------------------------------------------
  async getCompsForOrg(kOrgId: number): Promise<Comp[]> {
    const response = await this.api.get<Comp[]>(`/api/admin/korgs/${kOrgId}/comps`);
    return response.data;
  }

  // Alias for convenience
  async getComps(kOrgId: number): Promise<Comp[]> {
    return this.getCompsForOrg(kOrgId);
  }

  async getComp(compId: number): Promise<Comp> {
    const response = await this.api.get<Comp>(`/api/admin/comps/${compId}`);
    return response.data;
  }

  async createComp(kOrgId: number, comp: Partial<Comp>): Promise<Comp> {
    const response = await this.api.post<Comp>(`/api/admin/korgs/${kOrgId}/comps`, comp);
    return response.data;
  }

  // AdminCompController.UpdateComp's real route is PUT /api/admin/korgs/{korgId}/comps/{compId} —
  // this used to call PUT /api/admin/comps/{compId}, which matches no route in either
  // AdminCompController or AdminCompConfigController and 404'd on every edit.
  async updateComp(kOrgId: number, compId: number, comp: Partial<Comp>): Promise<void> {
    await this.api.put(`/api/admin/korgs/${kOrgId}/comps/${compId}`, comp);
  }

  async deleteComp(kOrgId: number,compId: number): Promise<void> {
    await this.api.delete(`/api/admin/korgs/${kOrgId}/comps/${compId}`);
  }

  // --------------------------------------------------------------------------
  // Company Config (AdminCompController)
  // --------------------------------------------------------------------------
  async getCompConfig(compId: number): Promise<CompConfig> {
    const response = await this.api.get<CompConfig>(`/api/admin/comps/${compId}/config`);
    return response.data;
  }

  async updateCompConfig(compId: number, config: CompConfig): Promise<void> {
    await this.api.put(`/api/admin/comps/${compId}/config`, config);
  }

  async testEpicorConnection(compId: number): Promise<{
    success: boolean;
    elapsedMs?: number;
    message: string;
  }> {
    const response = await this.api.get(`/api/admin/comps/${compId}/config/test-connection`);
    return response.data;
  }

  // E4: test credentials the user just typed, before they're saved. Blank Username/Password/
  // X-API-Key in `config` fall back to whatever's already stored, same as a real save would.
  async testEpicorConnectionCandidate(compId: number, config: CompConfig): Promise<{
    success: boolean;
    elapsedMs?: number;
    message: string;
  }> {
    const response = await this.api.post(`/api/admin/comps/${compId}/config/test-connection-candidate`, config);
    return response.data;
  }

  // Look a Drive folder ID up by name before saving it. A folder ID is an opaque string with no
  // way to eyeball whether it is right, and a wrong one is only discovered later when files are
  // filed somewhere unexpected. Read-only; saves nothing.
  //
  // Always resolves — a bad ID is a normal answer, not an error. Check `usable` before trusting it:
  // `exists` is also true for a file or a binned folder, which need different messages.
  // `unknown` means Drive itself could not be reached, so saving should still be allowed.
  async verifyDriveFolder(folderId: string): Promise<{
    usable: boolean;
    exists: boolean;
    name?: string | null;
    isFolder: boolean;
    trashed: boolean;
    webViewLink?: string | null;
    unknown: boolean;
    problem?: string | null;
  }> {
    const response = await this.api.get('/api/admin/comps/verify-drive-folder', {
      params: { folderId },
    });
    return response.data;
  }
  
  // Company Email Config
  async getCompEmailConfig(compId: number) {
    const response = await this.api.get(`/api/admin/comps/${compId}/email-config`);
    return response.data;
  }

  async upsertCompEmailConfig(compId: number, payload: any) {
    const response = await this.api.put(`/api/admin/comps/${compId}/email-config`, payload);
    return response.data;
  }

  // Company Email Templates
  async getCompEmailTemplates(compId: number) {
    const response = await this.api.get(`/api/admin/comps/${compId}/email-templates`);
    return response.data;
  }

  async upsertCompEmailTemplate(compId: number, payload: any) {
    const response = await this.api.put(`/api/admin/comps/${compId}/email-templates`, payload);
    return response.data;
  }

  async getCompEmailTemplatesByCustomer(compId: number, customerId: string) {
    const response = await this.api.get(`/api/admin/comps/${compId}/email-templates`, {
      params: { customerId },
    });
    return response.data;
  }

  async deleteCompEmailTemplate(compId: number, templateId: number) {
    const response = await this.api.delete(`/api/admin/comps/${compId}/email-templates/${templateId}`);
    return response.data;
  }

  async previewEmailTemplate(compId: number, templateName: string, tokens: Record<string, string>, customerId?: string) {
    const response = await this.api.post('/api/invoiceEmail/preview', {
      companyId: compId,
      templateName,
      tokens,
      customerId: customerId || null,
    });
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Invoice Status Dashboard (InvoicesController)
  // --------------------------------------------------------------------------
  async getInvoices(
    company: string,
    customerId?: string,
    invoiceNumber?: string,
    limit: number = 100,
    offset: number = 0,
    startDate?: string,
    endDate?: string,
    sortBy?: string,
    sortDir?: string,
    statusFilter?: string
  ): Promise<InvoicesResponse> {
    const params: any = { company, limit, offset };
    if (customerId) params.customerId = customerId;
    if (invoiceNumber) params.invoiceNumber = invoiceNumber;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (sortBy) params.sortBy = sortBy;
    if (sortDir) params.sortDir = sortDir;
    if (statusFilter) params.statusFilter = statusFilter;

    const response = await this.api.get<InvoicesResponse>('/api/invoices', { params });
    return response.data;
  }

  /**
   * T1-A follow-up, docs/epicor-load-plan.md — bounded, one-time check for invoices that have
   * never had their doc counts checked (doCount/poCount null on the list response), scoped to
   * just the invoice numbers passed in (the current page, so ≤ page size). Backend caps
   * concurrency at 3 and never re-checks an invoice that already has a value, so this cannot
   * reproduce the old per-row-on-every-load storm.
   */
  async refreshDocCounts(
    company: string,
    invoiceNumbers: number[],
  ): Promise<{ results: Record<string, { doCount: number; poCount: number; needsDO: boolean; needsPO: boolean; docCountsCheckedAt: string | null }> }> {
    const response = await this.api.post('/api/invoices/refresh-doc-counts', { company, invoiceNumbers });
    return response.data;
  }

  async getInvoiceDetail(id: string): Promise<InvoiceDetail> {
    const response = await this.api.get<InvoiceDetail>(`/api/invoices/${id}`);
    return response.data;
  }

  async searchCustomers(compId: number, search: string, limit = 20): Promise<{ customerId: string; customerName: string | null }[]> {
    const response = await this.api.get('/api/email-templates/customer-search', {
      params: { compId, search, limit },
    });
    return response.data;
  }

  /**
   * The customer's Invoice Options from Epicor. emailSubject / emailTo / emailCc are the live
   * KInvEmailSubject_c / KARInvEmailTo_c / KInvCCEmail_c values — Epicor is the only store for all
   * three, so the Email Configuration screen displays them read-only rather than keeping a copy.
   */
  async getCustomerInvoiceOptions(company: string, customerId: string): Promise<{
    signedDORequired: boolean;
    custPODocRequired: boolean;
    custPONumRequired: boolean;
    emailSubject: string;
    emailTo: string;
    emailCc: string;
    deliveryMethod: string;
  }> {
    const response = await this.api.get('/api/invoices/customer-options', {
      params: { company, customerId }
    });
    return response.data;
  }

  async createInvoice(payload: any): Promise<{ id: string; status: string }> {
    const response = await this.api.post('/api/invoices', payload);
    return response.data;
  }

  // PO backfill — scan sales orders with no CustPO attachment and match them against Drive.
  // Read-only; attaches nothing.
  //
  // scanId is generated by the caller so it can poll getScanProgress() for the same run while this
  // request is still in flight. signal lets the caller abort it — the backend takes the cancellation
  // through to the scan loop, so cancelling stops the work rather than just hiding it.
  // custId narrows the scan to one customer. Worth doing: Drive is searched once per customer, so a
  // single-customer batch costs one lookup instead of one per customer in the range.
  async scanOrdersMissingPo(
    company: string,
    from: string,
    to: string,
    limit = 25,
    scanId?: string,
    signal?: AbortSignal,
    custId?: string,
  ): Promise<OrderScanResult> {
    const response = await this.api.get('/api/PoMatch/orders', {
      params: { company, from, to, limit, scanId, custId: custId || undefined },
      signal,
    });
    return response.data;
  }

  // PO backfill — which customers have orders missing a PO, and how many. BAQ only, so this returns
  // in about a second where scanning the same range takes minutes. Read-only.
  async listCustomersMissingPo(
    company: string,
    from: string,
    to: string,
    signal?: AbortSignal,
  ): Promise<CustomerScanSummary> {
    const response = await this.api.get('/api/PoMatch/customers', {
      params: { company, from, to },
      signal,
    });
    return response.data;
  }

  // PO backfill — progress of an in-flight scan. active goes false once the scan ends for any reason.
  // phase is 'listing' (Epicor still returning the order list, no count yet) or 'matching'.
  async getScanProgress(scanId: string): Promise<{
    active: boolean;
    phase: string;
    current: number;
    total: number;
    label: string;
  }> {
    const response = await this.api.get('/api/PoMatch/scan-progress', { params: { scanId } });
    return response.data;
  }

  // PO backfill — re-match a single sales order. Read-only.
  async matchOrderPo(company: string, orderNum: number): Promise<PoOrderMatchResult> {
    const response = await this.api.get(`/api/PoMatch/orders/${orderNum}`, { params: { company } });
    return response.data;
  }

  // PO backfill — attach a PO PDF to the sales order (⚠️ writes to Epicor).
  // Safe to retry: the backend reports alreadyAttached rather than creating a duplicate.
  async attachPoToOrder(company: string, orderNum: number, fileId: string, fileName: string): Promise<PoAttachResult> {
    const response = await this.api.post('/api/PoMatch/attach', { company, orderNum, fileId, fileName });
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Email endpoints (InvoiceEmailController)
  // --------------------------------------------------------------------------
  async sendTemplatedEmail(payload: {
    companyId: number;
    templateName: string;
    tokens: Record<string, string>;
    pdfBase64?: string;
    pdfFileName?: string;
    driveUrl?: string;
    invoiceId?: string;
    customSubject?: string;
    customerId?: string;
    // Duplicate-send incident, 15 Sep 2026 — set true only after a person has confirmed a dialog
    // naming the previous sender and time. Without it, the server refuses (409) a send against an
    // invoice already ARCHIVED with an EmailSentAt.
    confirmResend?: boolean;
  }): Promise<{
    success: boolean;
    message: string;
    emailId?: string;
    // A 200 does NOT mean an email reached the customer. The API returns success for four
    // outcomes where nothing was sent — no address on file, portal/post/hand delivery, special
    // case, and NO INVOICE — and distinguishes them with these flags plus a specific `message`.
    // Callers must not collapse them into "sent successfully" (BR120 item xii).
    noEmail?: boolean;
    archived?: boolean;
    noInvoiceRequired?: boolean;
  }> {
    const response = await this.api.post('/api/invoiceEmail/send-templated', payload);
    return response.data;
  }

  // P2-5 (scaffold) — merge several of one customer's invoice PDFs into one and send in a single email.
  async mergeAndSendInvoices(payload: {
    companyId: number;
    templateName: string;
    driveUrls: string[];
    tokens: Record<string, string>;
    customerId?: string;
    customSubject?: string;
    mergedFileName?: string;
    invoiceIds?: string[];
  }): Promise<{
    merged: number;
    archived: number;
    outcome: string;
    // Duplicate-send incident, 15 Sep 2026 — invoices the server refused to include: already sent,
    // or claimed by a concurrent send/merge-and-send right now. `merged` can legitimately be 0
    // (outcome "AllAlreadySent"/"AllInProgress") — callers must not assume a send happened just
    // because the request succeeded.
    skipped?: { legalNumber: string; reason: string }[];
  }> {
    const response = await this.api.post('/api/invoiceEmail/merge-and-send', payload);
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Invoice Report (InvoiceReportController)
  // --------------------------------------------------------------------------
  async generateAndUploadInvoiceReport(company: string, invoiceNum: number): Promise<{ driveUrl?: string; message?: string }> {
    const response = await this.api.post('/api/InvoiceReport/generate-and-upload', { company, invoiceNum });
    return response.data;
  }

  /**
   * P2-2 (step 1) — Stage a manually-uploaded DO (PDF or image) to Drive without merging.
   * Returns the staged file id/name so the UI can offer a separate "Merge" action.
   */
  async stageInvoiceDo(
    company: string,
    invoiceNum: number,
    file: File,
  ): Promise<{ driveUrl: string; fileId: string; fileName: string }> {
    const form = new FormData();
    form.append('company', company);
    form.append('invoiceNum', String(invoiceNum));
    form.append('file', file);
    const response = await this.api.post('/api/InvoiceReport/stage-do', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /** P2-2 (step 2) — Merge a previously-staged DO into the invoice and upload the result. */
  async mergeStagedDo(
    company: string,
    invoiceNum: number,
    fileId: string,
    fileName: string,
  ): Promise<{ driveUrl?: string; message?: string }> {
    const response = await this.api.post('/api/InvoiceReport/merge-staged-do', {
      company, invoiceNum, fileId, fileName,
    });
    return response.data;
  }

  /**
   * Test cleanup — delete the generated/staged Drive files + processing-history logs for an
   * invoice and reset it to PENDING. Destructive; internal-only.
   */
  async clearInvoiceGeneration(
    company: string,
    invoiceNum: number,
  ): Promise<{ filesDeleted: number; logsDeleted: number; message: string }> {
    const response = await this.api.post('/api/InvoiceReport/clear-generation', { company, invoiceNum });
    return response.data;
  }

  /** Poll current PDF generation progress for a given invoice. */
  async getGenerationProgress(company: string, invoiceNum: number): Promise<{
    active: boolean;
    step: string;
    stepIndex: number;
    totalSteps: number;
  }> {
    const response = await this.api.get('/api/InvoiceReport/progress', {
      params: { company, invoiceNum },
    });
    return response.data;
  }

  /** Check how many DOs/POs exist in Epicor for this invoice WITHOUT generating anything. */
  async precheckRelatedFiles(company: string, invoiceNum: number): Promise<{
    doCount: number;
    poCount: number;
    doFileNames: string[];
    poFileNames: string[];
  }> {
    const response = await this.api.get('/api/InvoiceReport/do-precheck', {
      params: { company, invoiceNum },
    });
    return response.data;
  }

  /**
   * docs/po-drive-first-plan.md — preview of what the strict Drive-first PO lookup would find for
   * this invoice's customer + PO number. Read-only; does not attach or generate anything. `outcome`
   * is one of Found | NotFound | Ambiguous | NotConfigured | NoInput.
   */
  async getPoMatchPreview(company: string, invoiceNum: number): Promise<{
    outcome: string;
    summary: string;
    file: { id: string; name: string; webViewLink?: string } | null;
  }> {
    const response = await this.api.get('/api/InvoiceReport/po-match-preview', {
      params: { company, invoiceNum },
    });
    return response.data;
  }

  /**
   * When invoices last arrived from Epicor, and how many arrived today (BR120 item ii).
   * Proves the sync ran — not that it carried everything. Answering "Epicor posted N today"
   * requires asking Epicor for the same range, which this does not do.
   */
  async getSyncStatus(company: string): Promise<{
    // KAIROSTSAP-120 — derived from SyncRuns per company, not an in-memory singleton: survives a
    // restart, and a manual sync now updates it the same way a scheduled one does.
    lastFinishedUtc: string | null;
    nextExpectedUtc: string | null;
    syncedToday: number;
    error: string | null;
    // True when the Epicor credential auth-failure latch (Comps.EpicorAuthFailedAt, owned by
    // BaqOwningCompany) is currently set — read live, not inferred from the latest run's Outcome.
    // Pressing Sync again cannot help while this is true; the frontend should point at Company
    // Configuration instead of the generic retry wording.
    credentialsBlocked: boolean;
    // Rows since this company's last Outcome="ok", not a fixed window.
    consecutiveFailures: number;
    statusKnown: boolean;
    intervalMinutes: number;
    // docs/completeness-reconciliation-build-plan.md Step 5 — the Epicor-vs-portal count this
    // class doc-comment says is "separate". Null when this company has no SyncRuns row yet
    // (before this feature's first cycle) — render "could not check", never a green state.
    reconciliation: {
      checkedAtUtc: string;
      epicorCount: number | null;
      portalCount: number | null;
      outcome: string;
      missingSample: string | null;
      // Step 5 rule 4 — dd/MM/yyyy, no timezone shift (see the backend comment on why).
      windowStart: string;
      windowEnd: string;
    } | null;
    // docs/completeness-reconciliation-build-plan.md Step 6, leg B — COMPLETED/ARCHIVED invoices
    // with no DriveUrl on record. Pure DB count, independent of the Epicor sync entirely.
    driveGapCount: number;
  }> {
    const response = await this.api.get('/api/invoices/sync-status', {
      params: { company },
    });
    return response.data;
  }

  // docs/completeness-reconciliation-build-plan.md Step 5 — "Show history" list, most recent first.
  async getSyncRuns(company: string, limit = 10): Promise<Array<{
    id: string;
    runAtUtc: string;
    windowStart: string;
    windowEnd: string;
    epicorCount: number | null;
    portalCount: number | null;
    inserted: number;
    failed: number;
    orphanCount: number | null;
    missingSample: string | null;
    outcome: string;
    errorText: string | null;
  }>> {
    const response = await this.api.get('/api/sync/runs', {
      params: { company, limit },
    });
    return response.data;
  }

  async getEpicorInvoiceDetails(company: string, invoiceNumber: string): Promise<Record<string, any>> {
    const response = await this.api.get('/api/invoices/epicor-details', {
      params: { company, invoiceNumber },
    });
    return response.data;
  }

  async getEmailConfig(companyId: number): Promise<any> {
    const response = await this.api.get(`/api/invoiceEmail/config/${companyId}`);
    return response.data;
  }

  async setArchiveNotifications(companyId: number, enabled: boolean): Promise<any> {
    const response = await this.api.patch(
      `/api/invoiceEmail/config/${companyId}/archive-notifications`,
      { enabled }
    );
    return response.data;
  }

  async getUserPreferences(): Promise<{ sendArchiveNotifications: boolean }> {
    const response = await this.api.get('/api/user/me/preferences');
    return response.data;
  }

  async updateUserPreferences(prefs: { sendArchiveNotifications: boolean }): Promise<any> {
    const response = await this.api.patch('/api/user/me/preferences', prefs);
    return response.data;
  }

  async enableEmailTestMode(companyId: number, testEmailOverride: string): Promise<any> {
    const response = await this.api.post(
      `/api/invoiceEmail/testmode/${companyId}/enable`,
      { testEmailOverride }
    );
    return response.data;
  }

  async disableEmailTestMode(companyId: number): Promise<any> {
    const response = await this.api.post(`/api/invoiceEmail/testmode/${companyId}/disable`);
    return response.data;
  }

  async syncInvoices(company: string, startDate?: string, endDate?: string): Promise<{
    company: string;
    startDate: string;
    endDate: string;
    inserted: number;
    updated: number;
    failed: number;
    error?: string;
  }> {
    const response = await this.api.post('/api/sync/invoices', {
      company,
      startDate: startDate ?? null,
      endDate: endDate ?? null,
    });
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Email Templates (EmailTemplatesController) — accessible to all users
  // --------------------------------------------------------------------------

  /** Get all templates (default + all customer overrides) for a company. */
  async getEmailTemplates(compId: number): Promise<any[]> {
    const response = await this.api.get('/api/email-templates', { params: { compId } });
    return response.data;
  }

  /** Get the template that will actually be used for a specific customer.
   *  Returns { customerOverride, companyDefault, hasOverride }. */
  async getCustomerEmailTemplate(compId: number, customerId: string): Promise<{
    customerOverride: any | null;
    companyDefault: any | null;
    hasOverride: boolean;
  }> {
    const response = await this.api.get('/api/email-templates/customer', {
      params: { compId, customerId },
    });
    return response.data;
  }

  /** List all customers that have a custom template override for a company. */
  async getEmailTemplateOverrides(compId: number): Promise<{
    id: number;
    customerId: string;
    lastUpdated: string;
    customerName?: string | null;
  }[]> {
    const response = await this.api.get('/api/email-templates/overrides', { params: { compId } });
    return response.data;
  }

  /** Update company default template — superAdmin only. */
  async updateDefaultEmailTemplate(compId: number, template: any): Promise<any> {
    const response = await this.api.put('/api/email-templates/default', template, {
      params: { compId },
    });
    return response.data;
  }

  /** Save or update a customer-specific template override — any user. */
  async upsertCustomerEmailTemplate(compId: number, template: any): Promise<any> {
    const response = await this.api.put('/api/email-templates/customer-override', template, {
      params: { compId },
    });
    return response.data;
  }

  /** Delete a customer template override — any user. */
  async deleteCustomerEmailTemplate(compId: number, templateId: number): Promise<void> {
    await this.api.delete(`/api/email-templates/customer-override/${templateId}`, {
      params: { compId },
    });
  }
}

export const orchestratorApi = new OrchestratorApiService();